import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import ImageGallery from 'react-image-gallery';
import Drift from 'drift-zoom';

import useMediaQuery from '../../utils/useMediaQuery';
import { ReactComponent as ArrowLeftIcon } from '../../images/icons/arrowLeft.svg';
import { ReactComponent as ArrowRightIcon } from '../../images/icons/arrowRight.svg';
import { ReactComponent as PlayIcon } from '../../images/icons/play.svg';

import styles from './Gallery.module.scss';
import classNames from 'classnames';
import GalleryModal from '../GalleryModal';
import { Helmet } from 'react-helmet';
import loadVisibleComponent from '../../utils/loadVisibleComponent'; 
import VideoPlaceholder from '../VideoPlaceholder';

const MuxPlayer = loadVisibleComponent(() => import('@mux/mux-player-react'));

const Gallery = ({
  sticky = false,
  images,
  onImageGallerySlide,
  mainImgClassName = '',
  galleryTitle = '',
  affectsLCP = false,
}) => {
  const galleryEl = useRef(null);
  const paneContainerRef = useRef(null);
  const [paneContainerShown, setPaneContainerShown] = useState(false);
  const { isGreaterOrEqualTo, BREAKPOINTS } = useMediaQuery();
  const isExtraLargeBP = isGreaterOrEqualTo(BREAKPOINTS.EXTRA_LARGE);
  const isXXLBP = isGreaterOrEqualTo(BREAKPOINTS.EXTRA_EXTRA_LARGE);
  //Drift instances from previously applied events. Should be cleaned up img.onMouseLeave
  const drifters = useRef([]);
  const [imageShowIndex, setImageShowIndex] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [pausedVideos, setPausedVideos] = useState(new Set());
  
  const onSlide = (index) => {
    // Pause any currently playing video when navigating away
    if (videoSlideIndexes.includes(currentIndex) && !videoSlideIndexes.includes(index)) {
      setPausedVideos(prev => new Set([...prev, currentIndex]));
    }
    
    // If navigating to a video slide, remove it from paused set so it can play
    if (videoSlideIndexes.includes(index)) {
      setPausedVideos(prev => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
    }
    
    setCurrentIndex(index);
    if (typeof onImageGallerySlide === 'function') {
      onImageGallerySlide(index);
    }
  }

  const isOpenGalleryModal = typeof imageShowIndex === 'number';

  const initiateDrift = (e) => {
    if (isExtraLargeBP) {
      const driftInstance = new Drift(e.currentTarget, {
        paneContainer: paneContainerRef.current,
        inlinePane: false,
        touchDelay: isXXLBP ? 0 : 500,
        onShow: () => setPaneContainerShown(true),
        onHide: () => setPaneContainerShown(false),
      });

      if (drifters?.current) drifters.current.push(driftInstance);
    }
  };

  const cleanupDrifters = () => {
    if (drifters?.current?.length)
      drifters.current.forEach((driftInstance) => {
        driftInstance.destroy();
      });
  };
  const videoSlideIndexes = images.filter((slide) => slide.playbackId).map((slide) => slide.index);

  useEffect(() => {
    if (videoSlideIndexes.includes(currentIndex)) {
      setIsVideoLoading(true);
    }
  }, [currentIndex]);

  useEffect(() => {
    if (sticky) {
      window.addEventListener('scroll', updateGalleryPosition);
      window.addEventListener('resize', updateGalleryPosition);
      setTimeout(updateGalleryPosition, 100);
    }

    return () => {
      window.removeEventListener('scroll', updateGalleryPosition);
      window.removeEventListener('resize', updateGalleryPosition);
    };
  }, [sticky]);

  const updateGalleryPosition = () => {
    if (!(galleryEl && galleryEl.current)) {
      return;
    }

    const parentHeight = galleryEl.current.parentElement.parentElement.getBoundingClientRect().height;
    const height = galleryEl.current.getBoundingClientRect().height;
    const scrollableHeight = parentHeight - height;

    galleryEl.current.style.transform =
      'translateY(' + (window.scrollY >= scrollableHeight ? scrollableHeight + 'px' : window.scrollY + 'px') + ')';
  };

  const renderLeftNav = (onClick, disabled) => {
    if (disabled) {
      return;
    }

    return (
      <span className={styles.imageGalleryLeftNav} onClick={onClick}>
        <ArrowLeftIcon />
      </span>
    );
  };

  const renderRightNav = (onClick, disabled) => {
    if (disabled) {
      return;
    }

    return (
      <span className={styles.imageGalleryRightNav} onClick={onClick}>
        <ArrowRightIcon />
      </span>
    );
  };

  const renderThumbInner = (item) => {
    return (
      <div className={styles.thumbnailContainer}>
        <img
          src={item.thumbnail}
          alt={item.thumbnailAlt}
          title={item.thumbnailTitle}
          loading={item.thumbnailLoading}
        />
        {item.playbackId && (
          <div className={styles.playIconOverlay}>
            <PlayIcon />
          </div>
        )}
      </div>
    );
  };
  const customRender = (item) => {
    if (item.playbackId) {
      const shouldPause = item.index !== currentIndex || pausedVideos.has(item.index);

      const pausedProp = shouldPause ? { paused: true } : {};
      return (
        <div style={{ position: 'relative' }}>
          {/* VideoPlaceholder always rendered behind video player to avoid flickering */}
          <VideoPlaceholder />
          {/* Video player rendered on top when loading */}
          {isVideoLoading && (
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
              <MuxPlayer
                  {...pausedProp}
                  theme="classic"
                  playbackId={item.playbackId}
                  className={styles.player}
                  accentColor="#1173d4"
                />
            </div>
          )}
        </div>
      );
    }
    return (
      <>
        {affectsLCP && item.index === 0 && (
          <Helmet>
            <link rel="preload" href={item.original} as="image" fetchPriority="high" />
          </Helmet>
        )}
        <img
          className={classNames('image-gallery-image', mainImgClassName)}
          onMouseOver={initiateDrift}
          onMouseLeave={cleanupDrifters}
          src={item.original}
          alt={item.originalAlt}
          data-zoom={item.large}
          fetchPriority={affectsLCP && item.index === 0 ? 'high' : 'low'}
          srcSet={item.srcSet}
          sizes={item.sizes}
          title={item.originalTitle}
          width="100%"
          height="100%"
          onClick={() => setImageShowIndex(item.index || 0)}
        />
        {item.description && <span className="image-gallery-description">{item.description}</span>}
      </>
    );
  };
  return (
    <>
      {sticky ? (
        <div ref={galleryEl}>
          <ImageGallery
            renderItem={customRender}
            renderThumbInner={renderThumbInner}
            disableThumbnailScroll
            items={images}
            lazyLoad
            onSlide={onSlide}
            renderLeftNav={renderLeftNav}
            renderRightNav={renderRightNav}
            slideInterval={2000}
            stopPropagation
            showPlayButton={false}
            showFullscreenButton={false}
            sizes="width: 100, height: 200"
          />
        </div>
      ) : (
        <ImageGallery
          renderItem={customRender}
          renderThumbInner={renderThumbInner}
          disableThumbnailScroll
          items={images}
          lazyLoad
          onSlide={onSlide}
          renderLeftNav={renderLeftNav}
          renderRightNav={renderRightNav}
          slideInterval={2000}
          stopPropagation
          showPlayButton={false}
          showFullscreenButton={false}
          sizes="width: 100, height: 200"
        />
      )}
      <div
        className={classNames(styles.imgPaneContainer, { [styles.imgPaneContainerShown]: paneContainerShown })}
        ref={paneContainerRef}
      ></div>
      {isOpenGalleryModal && (
        <GalleryModal
          isOpened={isOpenGalleryModal}
          closeModal={() => setImageShowIndex(null)}
          imageIndex={imageShowIndex}
          title={galleryTitle}
          images={images}
          onImageGallerySlide={onImageGallerySlide}
          renderLeftNav={renderLeftNav}
          renderRightNav={renderRightNav}
        />
      )}
    </>
  );
};

Gallery.propTypes = {
  images: PropTypes.array,
  sticky: PropTypes.bool,
  onImageGallerySlide: PropTypes.func,
  mainImgClassName: PropTypes.string,
};

export default Gallery;
